import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Search, Wand2, Sparkles, BookOpen } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export default function Glossary() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [params, setParams] = useSearchParams();
  const [generating, setGenerating] = useState(false);
  const q = params.get("q") || "";
  const category = params.get("category") || "All";
  const letter = params.get("letter") || "";

  useEffect(() => {
    (async () => {
      try {
        const [e, c] = await Promise.all([api.listEntries(), api.categories()]);
        setEntries(e);
        setCategories(["All", ...(c.categories || [])]);
      } catch (err) { console.error(err); }
    })();
  }, []);

  const filtered = useMemo(() => {
    return entries.filter((e) => {
      if (category !== "All" && e.category !== category) return false;
      if (letter && !e.term.toLowerCase().startsWith(letter.toLowerCase())) return false;
      if (q) {
        const s = q.toLowerCase();
        return e.term.toLowerCase().includes(s) || e.definition.toLowerCase().includes(s);
      }
      return true;
    });
  }, [entries, q, category, letter]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((e) => {
      // Guard against missing or empty term strings — public contributions
      // can (rarely) slip through with unusual first characters. Fall back
      // to "#" so those entries still appear grouped under a non-letter key.
      const first = (e.term || "").trim().charAt(0).toUpperCase();
      const k = /[A-Z]/.test(first) ? first : "#";
      map[k] = map[k] || [];
      map[k].push(e);
    });
    return map;
  }, [filtered]);

  const availableLetters = useMemo(
    () => new Set(entries.map((e) => {
      const first = (e.term || "").trim().charAt(0).toUpperCase();
      return /[A-Z]/.test(first) ? first : "#";
    })),
    [entries]
  );

  const sortedLetters = useMemo(() => Object.keys(grouped).sort(), [grouped]);

  const setParam = (key, val) => {
    const next = new URLSearchParams(params);
    if (val && val !== "All") next.set(key, val); else next.delete(key);
    setParams(next);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-20" data-testid="glossary-page">
      <div className="mb-10">
        <div className="font-ui text-xs uppercase tracking-[0.3em] opacity-60 mb-3">The Reference</div>
        <h1 className="font-display text-5xl md:text-7xl">Glossary</h1>
        <p className="font-body text-lg opacity-80 mt-4 max-w-2xl">
          A definitions library for the curious cook — from <em>al dente</em> to <em>zest</em>, and quite a few surprises in between.
        </p>
      </div>

      {/* Buy the book — glossary only */}
      <div
        className="border border-black/20 p-6 md:p-7 mb-10 flex flex-col md:flex-row md:items-center gap-5 md:gap-8"
        style={{ background: "var(--cream, #faf6ef)" }}
        data-testid="buy-book-callout"
      >
        <div className="flex-1">
          <div className="font-ui text-[10px] uppercase tracking-[0.3em] opacity-60 mb-2">The book</div>
          <div className="font-display text-2xl md:text-3xl leading-tight">
            Figure It Out, Eat It or Leave It
          </div>
          <p className="font-body text-sm md:text-base opacity-80 mt-2 max-w-2xl">
            <strong>Please note:</strong> the book is a glossary of definitions only — it does <em>not</em> contain recipes, and not every entry has an associated image. Images and recipes on this site are companion material added by Dr. D.
          </p>
        </div>
        <a
          href="https://www.amazon.com.au/Figure-out-Eat-Leave-Glossary-ebook/dp/B0H9BSL29G/ref=tmm_kin_swatch_0"
          target="_blank"
          rel="noopener sponsored"
          className="btn-primary btn-accent shrink-0"
          data-testid="glossary-buy-book-button"
        >
          <BookOpen size={16} /> Buy the Kindle edition on Amazon
        </a>
      </div>

      {/* Search */}
      <form onSubmit={(e) => e.preventDefault()} className="relative max-w-2xl mb-8" data-testid="glossary-search-form">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 opacity-60" />
        <input
          className="input-editorial"
          placeholder="Search terms and definitions…"
          value={q}
          onChange={(e) => setParam("q", e.target.value)}
          data-testid="glossary-search-input"
        />
      </form>

      {/* Categories */}
      <div className="flex flex-wrap gap-2 mb-8" data-testid="category-filter">
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => setParam("category", c)}
            className={`pill ${category === c ? "pill-active" : ""}`}
            data-testid={`filter-category-${c.toLowerCase().replace(/\s+/g,'-')}`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Alphabet */}
      <div className="flex flex-wrap gap-1 mb-10 border-t border-b border-black/15 py-4" data-testid="alphabet-nav">
        <button
          onClick={() => setParam("letter", "")}
          className={`px-3 py-1 font-ui text-sm uppercase tracking-widest ${!letter ? "font-bold" : "opacity-60 hover:opacity-100"}`}
          data-testid="letter-all"
        >All</button>
        {ALPHABET.map((L) => {
          const has = availableLetters.has(L);
          const active = letter.toUpperCase() === L;
          return (
            <button
              key={L}
              disabled={!has}
              onClick={() => setParam("letter", L)}
              className={`w-8 h-8 font-display text-xl ${active ? "text-white" : "text-current"} ${!has ? "opacity-20 cursor-not-allowed" : "hover:opacity-100"}`}
              style={active ? { background: "var(--tomato)" } : {}}
              data-testid={`letter-${L}`}
            >
              {L}
            </button>
          );
        })}
      </div>

      {/* Results */}
      <div className="text-sm font-ui uppercase tracking-widest opacity-60 mb-6" data-testid="results-count">
        {filtered.length} term{filtered.length === 1 ? "" : "s"}
      </div>

      {filtered.length === 0 ? (
        <div className="border border-black/20 p-8 md:p-12" data-testid="no-results">
          <div className="flex items-start gap-4">
            <Sparkles className="mt-1 shrink-0" size={24} style={{ color: "var(--tomato)" }} />
            <div className="flex-1">
              <div className="font-display text-3xl md:text-4xl mb-2">No entry for {q ? <em>&ldquo;{q}&rdquo;</em> : "that filter"} yet.</div>
              <p className="font-body opacity-80 mb-6">
                {q
                  ? "Let Bite & Banter draft it in Dr. D's voice. The definition will be added to the dictionary immediately."
                  : "Try a different letter, clear the category, or search a specific term."}
              </p>
              {q && (
                <button
                  onClick={async () => {
                    setGenerating(true);
                    try {
                      const created = await api.generateEntry(q);
                      toast.success(`"${created.term}" added to the dictionary`);
                      navigate(`/term/${created.slug}`);
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Generation failed");
                    } finally { setGenerating(false); }
                  }}
                  disabled={generating}
                  className="btn-primary btn-accent disabled:opacity-50"
                  data-testid="generate-entry-btn"
                >
                  <Wand2 size={16} /> {generating ? "Writing entry…" : `Define "${q}" now`}
                </button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-16">
          {sortedLetters.map((L) => (
            <section key={L} className="grid md:grid-cols-12 gap-8" data-testid={`section-${L}`}>
              <div className="md:col-span-2">
                <div className="letter-mark text-8xl md:text-9xl sticky top-24">{L}</div>
              </div>
              <div className="md:col-span-10 grid md:grid-cols-2 gap-6 stagger">
                {grouped[L].map((e) => (
                  <Link
                    key={e.slug}
                    to={`/term/${e.slug}`}
                    className="card-editorial flex gap-4"
                    data-testid={`glossary-term-${e.slug}`}
                  >
                    <div className="w-32 shrink-0 aspect-square overflow-hidden">
                      <img src={e.image_url} alt={e.term} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 py-4 pr-4">
                      <div className="font-ui text-[10px] uppercase tracking-[0.25em] opacity-60 mb-1">{e.category}</div>
                      <div className="font-display text-3xl leading-none break-words">{e.term}</div>
                      {e.pronunciation && <div className="font-body italic text-xs opacity-70 mt-1">{e.pronunciation}</div>}
                      <p className="font-body text-sm mt-2 opacity-80 line-clamp-3">{e.definition}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
